import { AnimatePresence, motion as m } from "motion/react";
import type { BoardSlot } from "../../shared/game-types.js";
import { useMotionTuning } from "../lib/tuning.js";

interface BoardProps {
  slots: BoardSlot[];
  /** Rank revealed by the most recent correct answer, for the settle animation. */
  justFound: number | null;
  /** True while the board is filling itself in after the category closed. */
  cascading: boolean;
}

export function Board({ slots, justFound, cascading }: BoardProps) {
  const motion = useMotionTuning();
  const {
    spring_stiffness,
    spring_damping,
    flipDuration_ms,
    staggerFromClose_ms,
    riseDistance_px,
  } = motion.reveal;

  // During a cascade each unearned slot waits its turn; a slot won in play flips
  // immediately, because the player is waiting to see their own answer land.
  let cascadeIndex = 0;

  return (
    <div className="board">
      {slots.map((slot) => {
        const earned = Boolean(slot.foundBy);
        const delay =
          cascading && !earned
            ? (cascadeIndex++ * staggerFromClose_ms) / 1000
            : 0;

        return (
          <m.div
            key={slot.rank}
            className={[
              "slot",
              "teamTint",
              slot.revealed ? "slot--shown" : "slot--hidden",
              earned ? "slot--found" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            data-team={slot.foundBy?.teamId ?? "rose"}
            initial={false}
            animate={{
              rotateX: slot.revealed ? 0 : 0,
              scale:
                slot.rank === justFound ? [1, motion.correct.pulseScale, 1] : 1,
            }}
            transition={{
              scale: {
                duration: motion.correct.pulseDuration_ms / 1000,
                ease: [0.2, 0.9, 0.2, 1],
              },
            }}
          >
            <div className="slot__rank numeral">{slot.rank}</div>

            <div className="slot__body">
              <AnimatePresence mode="wait" initial={false}>
                {slot.revealed ? (
                  <m.div
                    key="shown"
                    initial={{ opacity: 0, y: riseDistance_px, rotateX: -55 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: spring_stiffness,
                      damping: spring_damping,
                      delay,
                      duration: flipDuration_ms / 1000,
                    }}
                    style={{ transformOrigin: "top center" }}
                  >
                    <div
                      className="slot__text display"
                      // Drives the length-aware type scale in tv.css so a long
                      // timeline entry shrinks instead of being ellipsised away.
                      style={{
                        ["--answer-len" as string]: slot.text?.length ?? 10,
                      }}
                    >
                      {slot.text}
                    </div>
                    {slot.value ? (
                      <div className="slot__value numeral">{slot.value}</div>
                    ) : null}
                  </m.div>
                ) : (
                  // Unrevealed stubs carry no markup at all — the ghosted
                  // question mark is printed into the card by CSS.
                  <m.div
                    key="hidden"
                    exit={{ opacity: 0 }}
                    aria-hidden="true"
                  />
                )}
              </AnimatePresence>
            </div>

            {/* The finder sits in the row, not under the answer: stacked below
                the value it squeezed a three-line body into a card sized for
                two, while the whole middle of the stub sat empty. */}
            {slot.revealed && slot.foundBy ? (
              <div className="slot__by">{slot.foundBy.playerName}</div>
            ) : null}

            <div className="slot__points numeral">{slot.points}</div>
          </m.div>
        );
      })}
    </div>
  );
}
